/**
 * Insurance copy formatting helpers
 *
 * Formats euro amounts and simple `{key}` templates for checkout tables and explain copy.
 */

/**
 * Formats a euro amount for display in checkout and explain UI.
 *
 * @param {number} amount Amount in euros.
 * @param {string} mode `plain`, `deduct` (leading minus), or `bonus` (leading plus).
 * @param {boolean} noDecimals Whole euros ("150 €", the price card) instead of the default two decimals ("150,00 €").
 * @return {string} Localized euro string with non-breaking space.
 */
export function formatEuro(amount: number, mode: 'plain' | 'deduct' | 'bonus', noDecimals = false): string {
  const formattedAmount = noDecimals ? amount.toFixed(0) : amount.toFixed(2).replace('.', ',');
  const base = `${formattedAmount}\u00A0€`;
  if (mode === 'deduct') {
    return `\u2013\u00A0${base}`;
  }
  if (mode === 'bonus') {
    return `+ ${formattedAmount}\u00A0€`;
  }
  return base;
}

/**
 * Replaces `{key}` placeholders in a template string.
 *
 * @param {string} template String with `{var}` placeholders.
 * @param {Object} vars Map of placeholder names to values.
 * @return {string} Interpolated string.
 */
export function applyTemplate(template: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce((result, [key, value]) => result.split(`{${key}}`).join(value), template);
}

/**
 * Formats a refund rate as a whole-number percentage string.
 *
 * @param {number} refundRate Decimal rate (e.g. 0.8 for 80%).
 * @return {string} Percentage label for UI.
 */
export function formatRefundPercent(refundRate: number): string {
  return `${Math.round(refundRate * 100)} %`;
}
